import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import PageContainer from "../../components/common/PageContainer";
import BaseTable from "../../components/table/BaseTable";
import Loader from "../../components/ui/Loader/Loader";
import FilterBar from "../../components/ui/FilterBar";
import FilterSelect from "../../components/ui/FilterSelect";
import Pagination from "../../components/common/pagination/Pagination";

import { useAgentsBillingQuery } from "../../queries/agents/useAgentsQuery";
import { formatDateDDMMYYYY } from "../../hooks/commonHooks";
import { formatVoiceClock, voiceUsagePercent } from "../../utils/voiceClock";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200];
const EMPTY_AGENTS = [];

const USAGE_FILTERS = [
  { label: "All Usage", value: "All" },
  { label: "Under 50%", value: "under-50" },
  { label: "50–89%", value: "50-89" },
  { label: "90–100%", value: "90-100" },
  { label: "Over 100%", value: "over-100" },
];

const STATUS_FILTERS = [
  { label: "All Statuses", value: "All" },
  { label: "No Overage", value: "No Overage" },
  { label: "Overage", value: "Overage" },
];

function formatInr(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return "—";
  return `₹${value.toLocaleString("en-IN")}`;
}

function usagePercentage(row) {
  return voiceUsagePercent(row.minutes_used, row.included_minutes);
}

function formatBillingCycle(cycle) {
  if (!cycle?.start || !cycle?.end) return "—";
  return `${formatDateDDMMYYYY(cycle.start)} – ${formatDateDDMMYYYY(cycle.end)}`;
}

function queryErrorMessage(error) {
  if (!error) return "Failed to load agent billing";
  if (typeof error === "string") return error;
  return error.message || "Failed to load agent billing";
}

export default function AgentsUsagePage() {
  const [filterPlan, setFilterPlan] = useState("All");
  const [filterUsage, setFilterUsage] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [knownPlans, setKnownPlans] = useState([]);

  const { data, isLoading, isError, error } = useAgentsBillingQuery({
    page: currentPage,
    page_size: pageSize,
    plan: filterPlan,
    status: filterStatus,
    usage: filterUsage,
  });

  const agents = data?.agents ?? EMPTY_AGENTS;
  const totalPages = data?.pagination?.total_pages ?? 1;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterPlan, filterUsage, filterStatus]);

  useEffect(() => {
    setKnownPlans((prev) => {
      const next = new Set(prev);
      let changed = false;

      agents.forEach((agent) => {
        if (agent.plan_name && !next.has(agent.plan_name)) {
          next.add(agent.plan_name);
          changed = true;
        }
      });

      if (filterPlan !== "All" && !next.has(filterPlan)) {
        next.add(filterPlan);
        changed = true;
      }

      return changed ? Array.from(next) : prev;
    });
  }, [agents, filterPlan]);

  const planOptions = [
    { label: "All Plans", value: "All" },
    ...knownPlans.map((plan) => ({ label: plan, value: plan })),
  ];

  if (isLoading && !data) return <Loader />;

  const columns = [
    {
      title: "Agent",
      dataIndex: "agent_name",
      render: (name, row) => (
        <div>
          <Link to={`/agent-usage/${row.agent_id}`}>
            <span className="fw-medium text-primary">{name}</span>
          </Link>
          <div className="text-sm text-muted">{row.email}</div>
        </div>
      ),
    },
    {
      title: "Plan",
      dataIndex: "plan_name",
    },
    {
      title: "Billing Cycle",
      dataIndex: "billing_cycle",
      render: (cycle) => formatBillingCycle(cycle),
    },
    {
      title: "Used",
      dataIndex: "minutes_used",
      render: (value) => formatVoiceClock(value),
    },
    {
      title: "Included",
      dataIndex: "included_minutes",
      render: (value) => formatVoiceClock(value),
    },
    {
      title: "Usage",
      dataIndex: "minutes_used",
      key: "usage",
      render: (_, row) => `${usagePercentage(row)}%`,
    },
    {
      title: "Remaining",
      dataIndex: "remaining_minutes",
      render: (value) => formatVoiceClock(value),
    },
    {
      title: "Extra",
      dataIndex: "extra_minutes",
      render: (value) => formatVoiceClock(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => <span className="badge badge-property">{status}</span>,
    },
    {
      title: "Estimated Bill",
      dataIndex: "estimated_bill_inr",
      render: (value) => <span className="fw-semibold">{formatInr(value)}</span>,
    },
  ];

  return (
    <PageContainer>
      <div className="d-flex justify-between align-center mb-3">
        <h2 className="text-primary m-0">Agent Usage</h2>
      </div>

      <FilterBar className="mb-3">
        <FilterSelect
          label="Plan"
          value={filterPlan}
          onChange={(e) => setFilterPlan(e.target.value)}
          options={planOptions}
        />
        <FilterSelect
          label="Usage"
          value={filterUsage}
          onChange={(e) => setFilterUsage(e.target.value)}
          options={USAGE_FILTERS}
        />
        <FilterSelect
          label="Status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          options={STATUS_FILTERS}
        />
      </FilterBar>

      {isError && (
        <p className="text-muted mb-3">{queryErrorMessage(error)}</p>
      )}

      <BaseTable columns={columns} data={agents} rowKey="agent_id" />

      <div className="d-flex align-center justify-between my-3">
        <div className="d-flex align-center gap-1">
          <label htmlFor="usage-page-size" className="text-muted">
            Per page:
          </label>
          <select
            id="usage-page-size"
            value={pageSize}
            className="filter-select"
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </PageContainer>
  );
}
